/**
 * One-glance per-platform health badge for a CG Boost campaign.
 *
 * Renders one of five states from a CgBoostPullStatus payload, optionally
 * clickable to open the diagnostics drawer. Uses Pill colors so the badge
 * sits naturally next to other status chips in the campaign header.
 */

import React from 'react';
import type { CgBoostPlatform, CgBoostPlatformHealth, CgBoostPullStatus } from '../../types/cgBoost';
import { PLATFORM_LABELS } from '../../services/cgBoostApi';
import { formatTimeAgo } from '../../utils/dateFormatters';
import { Pill } from '../ui';

type Tone = React.ComponentProps<typeof Pill>['tone'];

const HEALTH_TONE: Record<CgBoostPlatformHealth, Tone> = {
  not_linked: 'neutral',
  linked_no_data: 'info',
  healthy: 'success',
  stale: 'warning',
  erroring: 'danger',
};

const HEALTH_DOT: Record<CgBoostPlatformHealth, string> = {
  not_linked: 'var(--fg-4)',
  linked_no_data: 'var(--info)',
  healthy: 'var(--success)',
  stale: 'var(--warning)',
  erroring: 'var(--danger)',
};

const HEALTH_LABEL: Record<CgBoostPlatformHealth, string> = {
  not_linked: 'Not linked',
  linked_no_data: 'Linked, no data yet',
  healthy: 'Healthy',
  stale: 'Stale',
  erroring: 'Erroring',
};

interface Props {
  platform: CgBoostPlatform;
  status: CgBoostPullStatus;
  onClick?: () => void;
  /** Compact = dot + platform name only. Default = dot + platform + state. */
  compact?: boolean;
}

const PlatformHealthBadge: React.FC<Props> = ({ platform, status, onClick, compact }) => {
  const tone = HEALTH_TONE[status.health];
  const label = compact
    ? PLATFORM_LABELS[platform]
    : `${PLATFORM_LABELS[platform]} · ${HEALTH_LABEL[status.health]}`;

  return (
    <Pill
      tone={tone}
      size="sm"
      onClick={onClick}
      title={`${PLATFORM_LABELS[platform]} — ${HEALTH_LABEL[status.health]}${
        status.lastError ? `\nLast error: ${status.lastError}` : ''
      }${status.lastSuccessAt ? `\nLast success: ${formatTimeAgo(status.lastSuccessAt)}` : ''}`}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: HEALTH_DOT[status.health],
          flexShrink: 0,
        }}
      />
      <span>{label}</span>
    </Pill>
  );
};

export default PlatformHealthBadge;
